import { useEffect, useState } from 'react';
import { withStyles, CardTabs } from 'vopay';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Title, UtilButton } from 'UI';
import styles from './ContactPage.module.scss';
import CustomerOverview from '@/components/Customer/Overview/CustomerOverview';
import DetailsTab from '@/components/Customer/Tabs/Details/DetailsTab';
import SettingsTab from '@/components/Customer/Tabs/Settings/SettingsTab';
import PaymentMethodsTab from '@/components/Customer/Tabs/PaymentMethods/PaymentMethodsTab';
import ActivityTab from '@/components/Customer/Tabs/Activity/ActivityTab';
import useContacts from '@/hooks/useApi/useContacts';
import PaymentMethodDropdownMenu from '@/components/Customers/CustomersPage/PaymentMethodsDropdown/PaymentMethodsDropdown';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

const ContactPage = () => {
  const location = useLocation();
  const history = useHistory();
  const { contactID } = useParams<{ contactID: string }>();
  const { contact, fetchContact } = useContacts();
  const [tab, setTab] = useState('details');
  const { t } = useTranslation('translations', { keyPrefix: 'Contact' });
  const { t: tCustomer } = useTranslation('translations', { keyPrefix: 'Customers' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const newTab = search.get('cardTab');
    newTab && setTab(newTab);
  }, [location.search]);

  useEffect(() => {
    fetchContact(contactID);
  }, []);

  if (!contact) return null;

  async function handleUpdate() {
    return fetchContact(contactID);
  }

  return (
    <div className={cn('contactPage')}>
      <UtilButton
        iconLeft="chevron_left"
        size="large"
        onClick={() => {
          if (history.length > 1) {
            history.goBack();
          } else {
            history.replace('/contacts');
          }
        }}
      >
        {history.length > 1
          ? tGlobal('Buttons.back', {
              defaultValue: 'Back',
            })
          : t('contacts', {
              defaultValue: 'Contacts',
            })}
      </UtilButton>

      <div className={cn('pageHeader')}>
        <Title>
          {t('pageTitle', {
            defaultValue: 'Contact Details',
          })}
        </Title>

        <div className={cn('pageHeaderLinks')}>
          <PaymentMethodDropdownMenu
            accountName={contact.ContactName}
            accountID={contactID}
            email={contact.EmailAddress}
            disabled={!contact.IsActive}
          />
        </div>
      </div>

      <div className={cn('overview')}>
        <CustomerOverview
          accountID={contact.ContactID}
          active={contact.IsActive}
          accountName={contact.ContactName}
          lang={contact.Language}
          isBusiness={contact.IsBusiness}
        />
      </div>

      <CardTabs.Root value={tab} onValueChange={setTab}>
        <CardTabs.List
          aria-label={tCustomer('manageYourAccount', {
            defaultValue: 'Manage your account',
          })}
        >
          <CardTabs.Trigger value="details">
            {tCustomer('details', {
              defaultValue: 'Details',
            })}
          </CardTabs.Trigger>
          <CardTabs.Trigger value="activity">
            {tCustomer('activity', {
              defaultValue: 'Activity',
            })}
          </CardTabs.Trigger>
          <CardTabs.Trigger value="paymentMethods">
            {tCustomer('paymentMethods', {
              defaultValue: 'Payment Methods',
            })}
          </CardTabs.Trigger>
          {/* <CardTabs.Trigger value="settings">Settings</CardTabs.Trigger> */}
        </CardTabs.List>

        <CardTabs.Content value="details">
          <DetailsTab
            account={{
              ...contact,
              AccountID: contact.ContactID,
              AccountName: contact.ContactName,
            }}
            onUpdate={handleUpdate}
          />
        </CardTabs.Content>

        <CardTabs.Content value="activity">
          <ActivityTab accountID={contact.ContactID} active={contact.IsActive} />
        </CardTabs.Content>

        <CardTabs.Content value="paymentMethods" className={cn('paymentMethodsTab')}>
          <PaymentMethodsTab
            accountID={contact.ContactID}
            accountName={contact.ContactName}
            email={contact.EmailAddress}
            active={contact.IsActive}
          />
        </CardTabs.Content>

        <CardTabs.Content value="settings" className={cn('tabContent')}>
          <SettingsTab lang={contact.Language} />
        </CardTabs.Content>
      </CardTabs.Root>
    </div>
  );
};

export default ContactPage;
